import type { ReactNode } from 'react';
import {
  type ChecklistDraftRow,
  type DocumentType,
  documentLabel,
  emptyDraft,
  presetLengkap,
  ringkasDraf,
  toggleMustReturn,
  toggleSelected,
} from '../lib/task-documents';

interface Props {
  readonly rows: readonly ChecklistDraftRow[];
  readonly onChange: (rows: readonly ChecklistDraftRow[]) => void;
  readonly disabled?: boolean;
  /** Terbuka = daftar rinci ditampilkan. Dikendalikan pemanggil agar tidak ada hook di sini. */
  readonly rinci: boolean;
  readonly onRinciChange: (rinci: boolean) => void;
}

/**
 * Manajer menyiapkan berkas penagihan yang dibawa kurir — "tukar faktur".
 *
 * Arah tiap berkas ditampilkan sebagai pilihan, bukan disembunyikan sebagai aturan sistem:
 * berkas mana yang harus kembali berbeda antar pelanggan, dan yang tahu bedanya adalah manajer
 * yang menyiapkannya, bukan tabel bawaan di dalam kode.
 */
export function DocumentChecklistPicker({
  rows,
  onChange,
  disabled = false,
  rinci,
  onRinciChange,
}: Props): ReactNode {
  const dipilih = rows.filter((r) => r.selected);
  const wajibKembali = dipilih.filter((r) => r.mustReturn);

  return (
    <fieldset className="checklist" disabled={disabled}>
      <legend>Dokumen penagihan yang dibawa kurir</legend>

      {/*
       * Dua pilihan, bukan empat belas centang. Yang hampir selalu benar diletakkan lebih dulu
       * dan sudah terpilih; yang jarang berbeda tetap tersedia di bawahnya.
       */}
      <label className="checklist-row">
        <input
          type="radio"
          name="paket-dokumen"
          checked={!rinci}
          onChange={() => {
            onRinciChange(false);
            onChange(presetLengkap());
          }}
        />
        <span>
          Paket tukar faktur <span className="muted small">— {ringkasDraf(presetLengkap())}</span>
        </span>
      </label>

      <label className="checklist-row">
        <input
          type="radio"
          name="paket-dokumen"
          checked={rinci}
          onChange={() => {
            onRinciChange(true);
            onChange(emptyDraft());
          }}
        />
        <span>Pilih sendiri…</span>
      </label>

      {!rinci ? (
        <p className="small muted">
          Invoice, Faktur Pajak, Surat Jalan, Tanda Terima, Kuitansi, Surat Kuasa, dan Berita Acara
          Serah Terima. Yang bertanda &ldquo;harus kembali&rdquo; akan ditagih ke kurir setelah
          barang diantar.
        </p>
      ) : null}

      <ul className="checklist-items" hidden={!rinci}>
        {rows.map((row) => (
          <li key={row.documentType}>
            <label className="checklist-row">
              <input
                type="checkbox"
                checked={row.selected}
                onChange={() => onChange(toggleSelected(rows, row.documentType))}
              />
              <span>{documentLabel(row.documentType)}</span>
            </label>

            {/*
             * Pilihan arah hanya muncul untuk berkas yang dicentang. Menampilkannya selalu
             * membuat layar penuh kendali yang tidak berpengaruh apa pun.
             */}
            {row.selected ? (
              <label className="checklist-direction small">
                <input
                  type="checkbox"
                  checked={row.mustReturn}
                  onChange={() => onChange(toggleMustReturn(rows, row.documentType))}
                />
                <span>Harus kembali</span>
              </label>
            ) : null}
          </li>
        ))}
      </ul>

      {rinci ? (
        <p className="small muted" aria-live="polite">
          {dipilih.length === 0
            ? 'Belum ada berkas dipilih. Tugas tetap dapat dibuat tanpa dokumen.'
            : `${dipilih.length} berkas dibawa, ${wajibKembali.length} harus kembali.`}
        </p>
      ) : null}
    </fieldset>
  );
}

export type { DocumentType };
